import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, AlertCircle, Clock, Activity, Settings, Play, Pause } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ReminderSettings {
  id: number;
  enabled: boolean;
  sendWindowMinutes: number;
  dedupeHours: number;
  lastRunAt: string | null;
  lastRunSummary: {
    sessionsScanned: number;
    candidatesFound: number;
    remindersSent: number;
    skippedDueToRSVP: number;
    skippedDueToDedupe: number;
    failures: number;
  } | null;
}

interface HealthStatus {
  ok: boolean;
  enabled: boolean;
  lastRunAt: string | null;
  schedulerRunning: boolean;
  environment: string;
  timestamp: string;
}

interface RunNowResult {
  ok: boolean;
  success: boolean;
  mocked: boolean;
  summary: {
    sessionsScanned: number;
    candidatesFound: number;
    remindersSent: number;
    skippedDueToRSVP: number;
    skippedDueToDedupe: number;
    failures: number;
  };
  sampleRecipients?: Array<{
    parentEmail: string;
    kidName: string;
    sessionTitle: string;
  }>;
  runAt: string;
  error?: string;
}

export default function AdminReminders() {
  const [settings, setSettings] = useState<ReminderSettings | null>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRunResult, setLastRunResult] = useState<RunNowResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { toast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    enabled: true,
    sendWindowMinutes: 120,
    dedupeHours: 6,
  });

  // Load settings and health on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([loadSettings(), loadHealth()]);
  };

  const loadHealth = async () => {
    try {
      const response = await fetch('/api/admin/reminders/health', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.warn('Health check failed:', response.status);
        return;
      }
      
      const data = await response.json();
      
      if (data.ok) {
        setHealth(data);
      }
    } catch (err) {
      console.warn('Health check error:', err);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/reminders/settings', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        setError('Internal server error');
        return;
      }
      
      const data = await response.json();
      
      if (!data.ok) {
        setError('Internal server error');
        return;
      }
      
      setSettings(data.settings);
      setFormData({
        enabled: data.settings.enabled,
        sendWindowMinutes: data.settings.sendWindowMinutes,
        dedupeHours: data.settings.dedupeHours,
      });
    } catch (err) {
      setError('Internal server error');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);
      
      const response = await fetch('/api/admin/reminders/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        // Always show "Internal server error" for non-2xx responses
        setError('Internal server error');
        return;
      }
      
      const data = await response.json();
      
      if (!data.ok) {
        setError('Internal server error');
        return;
      }
      
      // Update settings state with response data
      setSettings(data.settings);
      
      // Show success marker for 2 seconds
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      
      // Always show success toast
      toast({
        description: 'Reminder settings saved successfully',
      });
    } catch (err) {
      setError('Internal server error');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    try {
      setRunning(true);
      setError(null);
      setLastRunResult(null);
      
      const response = await fetch('/api/admin/reminders/run-now', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        setError('Internal server error');
        return;
      }
      
      const data = await response.json();
      
      if (!data.ok) {
        setError('Internal server error');
        return;
      }
      
      setLastRunResult(data);
      
      // Reload settings and health to get updated info
      await Promise.all([loadSettings(), loadHealth()]);
      
      toast({
        description: `Reminder job completed. Sent ${data.summary.remindersSent} reminders.`,
      });
    } catch (err) {
      setError('Internal server error');
    } finally {
      setRunning(false);
    }
  };

  const formatLastRun = (lastRunAt: string | null) => {
    if (!lastRunAt) return 'Never';
    
    try {
      const date = new Date(lastRunAt);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (enabled: boolean, schedulerRunning: boolean) => {
    if (!enabled) {
      return <Badge variant="secondary" className="bg-gray-100 text-gray-700"><Pause className="w-3 h-3 mr-1" />Disabled</Badge>;
    }
    if (schedulerRunning) {
      return <Badge variant="default" className="bg-green-100 text-green-700"><Activity className="w-3 h-3 mr-1" />Active</Badge>;
    }
    return <Badge variant="outline" className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Idle</Badge>;
  };

  const getLastRunBadge = (lastRunSummary: ReminderSettings['lastRunSummary']) => {
    if (!lastRunSummary) {
      return <Badge variant="outline">No runs yet</Badge>;
    }
    
    if (lastRunSummary.failures > 0) {
      return <Badge variant="destructive">Failed ({lastRunSummary.failures} errors)</Badge>;
    }
    
    if (lastRunSummary.remindersSent > 0) {
      return <Badge variant="default" className="bg-green-100 text-green-700">Success ({lastRunSummary.remindersSent} sent)</Badge>;
    }
    
    return <Badge variant="secondary">No reminders needed</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8" data-testid="admin-reminders-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading reminder settings...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-6xl" data-testid="admin-reminders-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Admin Reminders</h1>
        <p className="text-muted-foreground mt-2">
          Configure and monitor automatic session reminders for parents
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6" data-testid="admin-reminders-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {saveSuccess && (
        <Alert className="mb-6 border-green-200 bg-green-50" data-testid="admin-reminders-save-success">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Settings saved successfully
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Health Panel - Full width on mobile, 1/3 on desktop */}
        <Card className="lg:col-span-1" data-testid="admin-reminders-health-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Health
            </CardTitle>
            <CardDescription>
              Real-time status of the reminder system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {health ? (
              <>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Status</Label>
                  {getStatusBadge(health.enabled, health.schedulerRunning)}
                </div>
                
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Environment</Label>
                  <Badge variant="outline" className="text-xs">
                    {health.environment}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Scheduler</Label>
                  <Badge variant={health.schedulerRunning ? "default" : "secondary"}>
                    {health.schedulerRunning ? "Running" : "Stopped"}
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Last Run</Label>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {formatLastRun(settings?.lastRunAt)}
                    </span>
                  </div>
                  {settings?.lastRunSummary && (
                    <div className="mt-2">
                      {getLastRunBadge(settings.lastRunSummary)}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Loading health status...</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Settings and Actions - 2/3 width on desktop */}
        <div className="lg:col-span-2 space-y-6">
          {/* Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configuration
              </CardTitle>
              <CardDescription>
                Control when and how reminders are sent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enabled Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enabled">Enable Reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Turn automatic reminders on or off
                  </p>
                </div>
                <Switch
                  id="enabled"
                  data-testid="admin-reminders-enabled-toggle"
                  checked={formData.enabled}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, enabled: checked })
                  }
                />
              </div>

              {/* Send Window */}
              <div className="space-y-2">
                <Label htmlFor="sendWindow">Send Window (minutes)</Label>
                <Input
                  id="sendWindow"
                  data-testid="admin-reminders-send-window"
                  type="number"
                  min="1"
                  max="1440"
                  value={formData.sendWindowMinutes}
                  onChange={(e) => {
                    const value = Math.max(1, Math.min(1440, parseInt(e.target.value) || 1));
                    setFormData({
                      ...formData,
                      sendWindowMinutes: value,
                    });
                  }}
                />
                <p className="text-sm text-muted-foreground">
                  Send reminders for sessions starting within this many minutes (1-1440)
                </p>
              </div>

              {/* Dedupe Hours */}
              <div className="space-y-2">
                <Label htmlFor="dedupeHours">Dedupe Hours</Label>
                <Input
                  id="dedupeHours"
                  data-testid="admin-reminders-dedupe-hours"
                  type="number"
                  min="0"
                  max="168"
                  value={formData.dedupeHours}
                  onChange={(e) => {
                    const value = Math.max(0, Math.min(168, parseInt(e.target.value) || 0));
                    setFormData({
                      ...formData,
                      dedupeHours: value,
                    });
                  }}
                />
                <p className="text-sm text-muted-foreground">
                  Wait this many hours before sending duplicate reminders
                </p>
              </div>

              {/* Save Button */}
              <Button
                data-testid="admin-reminders-save"
                onClick={saveSettings}
                disabled={saving}
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5" />
                Manual Actions
              </CardTitle>
              <CardDescription>
                Trigger manual runs and view results
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Last Run Details */}
              {settings?.lastRunSummary && (
                <div className="space-y-3" data-testid="admin-reminders-last-run-details">
                  <Label>Last Run Summary</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                    <div className="space-y-1">
                      <div className="text-muted-foreground">Sessions Scanned</div>
                      <div className="font-medium">{settings.lastRunSummary.sessionsScanned}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-muted-foreground">Reminders Sent</div>
                      <div className="font-medium text-green-600">{settings.lastRunSummary.remindersSent}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-muted-foreground">Skipped (RSVP)</div>
                      <div className="font-medium">{settings.lastRunSummary.skippedDueToRSVP}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-muted-foreground">Skipped (Dedupe)</div>
                      <div className="font-medium">{settings.lastRunSummary.skippedDueToDedupe}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-muted-foreground">Candidates Found</div>
                      <div className="font-medium">{settings.lastRunSummary.candidatesFound}</div>
                    </div>
                    {settings.lastRunSummary.failures > 0 && (
                      <div className="space-y-1">
                        <div className="text-muted-foreground">Failures</div>
                        <div className="font-medium text-red-600">{settings.lastRunSummary.failures}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Run Now Button */}
              <Button
                data-testid="admin-reminders-run-now"
                onClick={runNow}
                disabled={running}
                variant="outline"
                className="w-full"
              >
                {running ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Run Now
                  </>
                )}
              </Button>

              {/* Last Run Result */}
              {lastRunResult && (
                <Alert className={lastRunResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'} data-testid="admin-reminders-run-result">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="font-medium flex items-center gap-2">
                        {lastRunResult.success ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                        {lastRunResult.success ? 'Success' : 'Failed'}
                        {lastRunResult.mocked && (
                          <Badge variant="outline" className="text-xs">Mocked</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                        <div>Sessions: {lastRunResult.summary.sessionsScanned}</div>
                        <div>Sent: {lastRunResult.summary.remindersSent}</div>
                        <div>Skipped: {lastRunResult.summary.skippedDueToRSVP + lastRunResult.summary.skippedDueToDedupe}</div>
                        {lastRunResult.summary.failures > 0 && (
                          <div className="text-red-600">
                            Failures: {lastRunResult.summary.failures}
                          </div>
                        )}
                      </div>
                      {lastRunResult.error && (
                        <div className="text-red-600 text-sm">
                          Error: {lastRunResult.error}
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}